<script setup lang="ts">
import RcCustomerMinersSelect from '@/components/rc/common/RcCustomerMinersSelect.vue'
import RcWoMinersSelect from '@/components/rc/common/RcWOMinersSelect.vue'
import MinersConsolidationGridCell from '@/components/rc/miner-repairs/consolidation/MinersConsolidationGridCell.vue'
import { NotifyErrorWrapper } from '@/helpers/core/notifications'
import { toPartsDict } from '@/helpers/rc/miners'
import { getMinerDetails } from '@/services/rc.service'
import { useOIDCStore } from '@/stores/oidc.store'
import type { IDetailsPart, IMinerIdentifier } from '@/types/rc/miners'
import { IndividualPart, PartType, partTypes } from '@/types/rc/miners/PartType'
import { Icon } from '@iconify/vue'
import { useQuery } from '@tanstack/vue-query'
import { computed, ref, watch } from 'vue'

// vue-dndrop callback argument
interface DragResult<T> {
  payload: T
  isSource: boolean
  willAcceptDrop: boolean
}

type Drag = DragResult<unknown>

const emit = defineEmits(['minerSelected', 'dragStart', 'dragEnd', 'dragSnap', 'dragUnsnap', 'drop'])

const props = withDefaults(
  defineProps<{
    column: number // the column number
    color: string // the column's color
    preventRefetch: boolean // whether to prevent refetching miner details (edit mode)
    rowPadding?: number // the row padding for the grid (amount of rows above these)
    preset?: number // the preset miner id
    exclude?: number[] // the miner ids to exclude from lookup (in case they have been selected already)
    active?: PartType // the active part type (the one being dragged)
    customerId?: string // use either customerId or WO to scope the miners selection
    workOrderNumber?: string // use either customerId or WO to scope the miners selection
  }>(),
  {
    rowPadding: 3,
    preset: undefined,
    exclude: undefined,
    active: undefined,
    customerId: undefined,
    workOrderNumber: undefined,
  }
)

const { isRcAppManagerUser } = useOIDCStore()

// Whether this column has been cleared
const cleared = ref<boolean>(false)

// IndividualPart values without Miner
const partsIndex = Object.values(IndividualPart).filter((individualPart) => individualPart !== IndividualPart.Miner)

// Model for rc-wo-miners-select
const selectedMinerId = ref<IMinerIdentifier | undefined>()

// Managers-only check to consolidate miners across WOs from the same customer
const fromAnotherRWO = ref(false)

// Resolved miner id to fetch details
const resolvedId = computed(() => {
  if (selectedMinerId.value?.id) return selectedMinerId.value.id
  if (!cleared.value) return props.preset
  return undefined
})

// Parts this miner has - each is a IDetailsPart with color and key (IndividualPart)
export type IConsolidationPart = IDetailsPart & { color: string }
export type IConsolidationPartsDict = Record<IndividualPart, IConsolidationPart>

// undefined signals absence of miner (slots are not rendered),
// while an empty object signals all parts missing (slots are rendered).
// Parts without id signal empty slots too.
const minerParts = defineModel<IConsolidationPartsDict | undefined>({ required: true })

// Query to fetch miner details. Ideally should go in composable but that needs refactor.
const { data: minerDetails, isFetching: minerDetailsFetching } = useQuery({
  enabled: () => !!resolvedId.value && minerParts.value && !props.preventRefetch,
  queryKey: ['miner-details', resolvedId],
  queryFn: () => getMinerDetails(resolvedId.value!).catch(NotifyErrorWrapper('Failed to fetch miner details')),
})

// Clear the miner selection, parts, everything...
const onClearMiner = () => {
  cleared.value = true
  minerParts.value = undefined
  selectedMinerId.value = undefined
}

// Load the miner. Called from the parent when the dialog is opened and from watch when minerDetails change.
const load = () => {
  if (!minerDetails.value) return
  // Update the minerParts model with the dict of parts.
  minerParts.value = minerDetails.value ? toPartsDict(minerDetails.value, { color: props.color }) : ({} as any)
  // In case of preset, update the selectedMinerId to the preset value so it's properly displayed in the dropdown header.
  if (props.preset) {
    selectedMinerId.value = minerDetails.value ? minerDetails.value : undefined
  }
}

// Watch for minerDetails changes. Cannot use computed since it's immutable, and we'll need to update this.
watch(minerDetails, load)

// Reset selection from parent
const reset = () => {
  minerParts.value = minerDetails.value
    ? (toPartsDict(minerDetails.value, { color: props.color }) as IConsolidationPartsDict)
    : undefined
}

const clear = () => {
  cleared.value = false
  minerParts.value = undefined
  selectedMinerId.value = undefined
}

// Exposing the resolvedId does indeed seem the cleanest here
defineExpose({ reset, clear, load, resolvedId })
</script>

<template>
  <!-- Header -->
  <div
    class="row-start-1 flex items-center justify-between"
    :style="`grid-column-start: ${column + 1}; color: ${color}`"
  >
    <span class="flex items-center gap-2 font-extralight">
      <icon :height="12" icon="mdi:circle" /> MINER {{ column }}
    </span>
    <span v-if="isRcAppManagerUser" class="flex flex-center gap-2">
      <el-text class="text-text-tertiary">From another RWO</el-text>
      <el-checkbox
        v-model="fromAnotherRWO"
        @change="
          (checked) => {
            if (!checked) clear()
          }
        "
      />
    </span>
  </div>

  <!-- Miner select dropdown for set RWO -->
  <!-- :preset and :exclude are fallthrough -->
  <RcWoMinersSelect
    v-if="!fromAnotherRWO"
    v-model="selectedMinerId"
    :work-order-number="workOrderNumber"
    class="row-start-2"
    :style="`grid-column-start: ${column + 1}`"
    placeholder="Select SN"
    :multiple="false"
    :preset="preset"
    :exclude="exclude"
    @update:model-value="emit('minerSelected', column, selectedMinerId)"
    @clear="onClearMiner"
  />

  <!-- Miner select dropdown for RWO's customer_id -->
  <RcCustomerMinersSelect
    v-if="fromAnotherRWO"
    v-model="selectedMinerId"
    :customer-id="customerId"
    class="row-start-2"
    :style="`grid-column-start: ${column + 1}`"
    placeholder="Select SN"
    :multiple="false"
    :preset="preset"
    :exclude="exclude"
    @update:model-value="emit('minerSelected', column, selectedMinerId)"
    @clear="onClearMiner"
  />

  <!-- A draggable container with a single element per IndividualPart -->
  <!-- @events and :style are fallthrough -->
  <template v-if="minerParts">
    <MinersConsolidationGridCell
      v-for="(partName, index) in partsIndex"
      :key="partName"
      :part-name="partName"
      :miner-parts="minerParts"
      :active-drag-type="active"
      :style="`grid-row-start: ${index + rowPadding}; grid-column-start: ${column + 1}; opacity: ${active && active !== partTypes[partName] ? 0.5 : 1}`"
      @drag-enter="emit('dragSnap', partName, column)"
      @drag-leave="emit('dragUnsnap', partName, column)"
      @drag-start="(drag: Drag) => drag.isSource && emit('dragStart', partName, column, drag)"
      @drag-end="(drag: Drag) => drag.isSource && emit('dragEnd', partName, column, drag)"
      @drop="(dropped: any) => emit('drop', partName, column, dropped)"
      @drop-not-allowed="() => console.warn('Drop not allowed')"
    />
  </template>

  <div
    v-if="!minerParts && minerDetailsFetching"
    class="flex items-center justify-center"
    :style="`grid-column-start: ${column + 1}; grid-row-start: ${rowPadding}; grid-row-end: ${rowPadding + partsIndex.length};`"
  >
    <icon icon="eos-icons:three-dots-loading" height="20" />
  </div>
</template>
